/**
 * Conversation navigator for the Flux Agent page: start a coaching (or, for trainers, training)
 * conversation, switch between recent conversations, and delete one after confirmation. Trainer
 * conversations carry a visible "Training" badge.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentConversationList
 */

import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { GraduationCap, Loader2, MessageSquarePlus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { FluxAgentConversation, FluxAgentMode } from '@/lib/fluxAgentApi';
import { cn } from '@/lib/utils';
import { parseServerTime } from '@/lib/serverTime';

/** Inputs for {@link FluxAgentConversationList}. */
export interface FluxAgentConversationListProps {
  /** Conversations, newest first. */
  conversations: FluxAgentConversation[];
  /** Currently open conversation id, if any. */
  activeId: string | null;
  /** True while the list is loading for the first time. */
  loading: boolean;
  /** True for framework trainers; enables the Coaching/Training mode switch. */
  trainer: boolean;
  /** True while a new conversation is being created. */
  creating: boolean;
  /** Open an existing conversation. */
  onSelect: (id: string) => void;
  /** Create a conversation in the chosen mode. */
  onCreate: (mode: FluxAgentMode) => void;
  /** Delete a conversation; the caller confirms with the user first. */
  onDelete: (conversation: FluxAgentConversation) => void;
}

/** Relative label such as "5 minutes ago" for a server timestamp; empty when it cannot be parsed. */
function relativeTime(iso: string): string {
  const date = parseServerTime(iso);
  return Number.isNaN(date.getTime()) ? '' : formatDistanceToNow(date, { addSuffix: true });
}

/** Render the new-conversation control and the selectable, deletable conversation list. */
export function FluxAgentConversationList({
  conversations,
  activeId,
  loading,
  trainer,
  creating,
  onSelect,
  onCreate,
  onDelete,
}: FluxAgentConversationListProps) {
  const [mode, setMode] = useState<FluxAgentMode>('coach');

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {trainer && (
        <div
          role="group"
          aria-label="New conversation mode"
          className="bg-muted grid grid-cols-2 gap-1 rounded-md p-1 text-xs"
        >
          {(
            [
              ['coach', 'Coaching'],
              ['trainer', 'Training mode'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              onClick={() => setMode(value)}
              className={cn(
                'focus-visible:ring-ring rounded px-2 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2',
                mode === value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <Button
        type="button"
        onClick={() => onCreate(mode)}
        disabled={creating}
        className="w-full gap-2"
      >
        {creating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : mode === 'trainer' ? (
          <GraduationCap className="h-4 w-4" />
        ) : (
          <MessageSquarePlus className="h-4 w-4" />
        )}
        {mode === 'trainer' ? 'New training session' : 'New conversation'}
      </Button>

      <nav
        aria-label="Flux Agent conversations"
        className="native-scroll -mx-1 min-h-0 flex-1 overflow-y-auto px-1"
      >
        {loading ? (
          <p className="text-muted-foreground flex items-center gap-2 px-2 py-3 text-xs">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading conversations…
          </p>
        ) : conversations.length === 0 ? (
          <p className="text-muted-foreground px-2 py-3 text-xs">
            No conversations yet. Say hi to get started.
          </p>
        ) : (
          <ul className="space-y-1">
            {conversations.map(conversation => {
              const active = conversation.id === activeId;
              return (
                <li key={conversation.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => onSelect(conversation.id)}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'focus-visible:ring-ring flex w-full flex-col items-start gap-0.5 rounded-md px-3 py-2 pr-10 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2',
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <span className="flex w-full items-center gap-2">
                      <span className="truncate font-medium">
                        {conversation.title || 'New conversation'}
                      </span>
                      {conversation.mode === 'trainer' && (
                        <Badge
                          variant={active ? 'secondary' : 'outline'}
                          className="shrink-0 px-1.5 py-0 text-[10px]"
                        >
                          Training
                        </Badge>
                      )}
                    </span>
                    <span
                      className={cn(
                        'text-[11px]',
                        active ? 'text-primary-foreground/75' : 'text-muted-foreground'
                      )}
                    >
                      {relativeTime(conversation.updatedAt)}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(conversation)}
                    aria-label={`Delete conversation ${conversation.title || 'New conversation'}`}
                    className={cn(
                      'focus-visible:ring-ring absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 md:opacity-0 md:group-hover:opacity-70',
                      active
                        ? 'text-primary-foreground hover:bg-primary-foreground/15 md:opacity-70'
                        : 'text-muted-foreground hover:bg-background'
                    )}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </nav>
    </div>
  );
}
